import type { SymbolId } from './types';

export interface SymbolArtwork {
  readonly id: SymbolId;
  readonly name: string;
  readonly shortLabel: string;
  readonly imageUrl?: string;
  readonly accent: number;
  readonly tier: 'low' | 'mid' | 'high' | 'wild' | 'scatter';
}

const IMAGE_ROOT = '/assets/images/optimized';
export const YUGI_HIGH_FIDELITY_URL = '/assets/images/master/yugi.png';

export const SYMBOL_ARTWORK: Readonly<Record<SymbolId, SymbolArtwork>> = {
  BEWD: {
    id: 'BEWD', name: 'Weißer Drache mit eiskaltem Blick', shortLabel: 'BEWD',
    imageUrl: `${IMAGE_ROOT}/white-dragon.webp`, accent: 0x93dcff, tier: 'high',
  },
  DM: {
    id: 'DM', name: 'Dunkles Magier-Mädchen', shortLabel: 'DM',
    imageUrl: `${IMAGE_ROOT}/dark-magician-girl.webp`, accent: 0xff8bd8, tier: 'high',
  },
  JINZO: {
    id: 'JINZO', name: 'Jinzo', shortLabel: 'JINZO',
    imageUrl: `${IMAGE_ROOT}/jinzo.webp`, accent: 0xd69cff, tier: 'mid',
  },
  TW: {
    id: 'TW', name: 'Zeitzauberer', shortLabel: 'ZEIT',
    imageUrl: `${IMAGE_ROOT}/time-wizard.webp`, accent: 0xffc76a, tier: 'mid',
  },
  ACE: { id: 'ACE', name: 'Ass', shortLabel: 'A', accent: 0xe8c978, tier: 'low' },
  KING: { id: 'KING', name: 'König', shortLabel: 'K', accent: 0xe1b95a, tier: 'low' },
  QUEEN: { id: 'QUEEN', name: 'Dame', shortLabel: 'D', accent: 0xc8a750, tier: 'low' },
  JACK: { id: 'JACK', name: 'Bube', shortLabel: 'B', accent: 0xb89045, tier: 'low' },
  WILD: {
    id: 'WILD', name: 'Yugi-Wild', shortLabel: 'WILD',
    // Keep Yami Yugi lossless: the 500×500 master contains fine face, hair and chain linework
    // that becomes visibly softer after the otherwise useful WebP delivery conversion.
    imageUrl: YUGI_HIGH_FIDELITY_URL, accent: 0xffd45f, tier: 'wild',
  },
  SCATTER: {
    id: 'SCATTER', name: 'Puzzle-Scatter', shortLabel: 'SCATTER',
    imageUrl: `${IMAGE_ROOT}/puzzle-scatter.webp`, accent: 0xff6f7f, tier: 'scatter',
  },
};

export const CARD_BACK_URL = `${IMAGE_ROOT}/card-back.webp`;
export const POT_OF_GREED_URL = `${IMAGE_ROOT}/pot-of-greed.webp`;
export const EGYPTIAN_GOD_URLS = Object.freeze({
  RA: '/assets/images/gods/ra.png',
  SLIFER: '/assets/images/gods/slifer.png',
  OBELISK: '/assets/images/gods/obelisk.webp',
} as const);
export const BRAND_LOGO_URL = `${IMAGE_ROOT}/yugioh-logo.webp`;

export const FEATURE_CHARACTER_URLS = {
  solomon: `${IMAGE_ROOT}/solomon-muto.webp`,
  joey: `${IMAGE_ROOT}/joey.webp`,
  seto: `${IMAGE_ROOT}/seto-kaiba.webp`,
  marik: `${IMAGE_ROOT}/marik.webp`,
} as const;

export const RUNTIME_IMAGE_URLS = [
  ...Object.values(SYMBOL_ARTWORK).flatMap((art) => art.imageUrl === undefined ? [] : [art.imageUrl]),
  CARD_BACK_URL,
  POT_OF_GREED_URL,
  ...Object.values(EGYPTIAN_GOD_URLS),
  BRAND_LOGO_URL,
  ...Object.values(FEATURE_CHARACTER_URLS),
] as const;

export const HIGH_FIDELITY_ARTWORK_URLS: ReadonlySet<string> = new Set([
  YUGI_HIGH_FIDELITY_URL,
]);
